import type { EnumMetadata } from "./model/EnumMetadata";
import { AjaxFetch } from "../api/infra/AjaxFetch";
import { EnumerationsLogic } from "./EnumerationsLogic";
import { Translations } from "./Translations";
import { Validation } from "../util/Validation";

/** Settings object that is injectable to Custom Elements based sxy-forms. */
export class SxyFormsSettings {

  /** Resolves an URL (shortened schema document URL) as an absolute URL string. */
  public resolveUrl(url: string): string {
    if (url.startsWith("/")) {
      if (!window) {
        throw new Error(`Relative url not supported outside browser scope: '${url}'.`);
      }
      const urlArr = window.location.href.split("/");
      url = `${urlArr[0]}//${urlArr[2]}${url}`;
    } else {
      url = url.replace(/^salaxy-secure\//g, `${new AjaxFetch().getServerAddress()}/`);
    }
    return url;
  }

  /**
   * Gets a custom validator for the given element.
   * If none is returned, the default validators are used.
   * If you want to disable default validators, you should return a function that returns null.
   * @param element Element for which the validator is fetched.
   * @param element.validator Validator is a string identifying the validator (attribute in the input component).
   * @param element.getRequire Function that returns true if the input is required.
   * @returns A custom validator function or undefined if the default validators should be used.
   * Validator function takes the value (bool and nmber are currently typed) and returns and error message or null if the value is valid.
   * Message is typically a key for a translation.
   */
  public getValidator(element: { validator: string, getRequire: () => boolean }): ((value: any) => string | undefined) | undefined {
    if (!element.validator) {
      return undefined;
    }

    const validators = {
      sxyCompanyIdFi: (value: any) => !Validation.formatCompanyIdFi(value),
      sxyCurrency: (value: any) => !Validation.isCurrency(value),
      sxyEmail: (value: any) => !Validation.isEmail(value),
      sxyIban: (value: any) => !Validation.isIban(value),
      sxyInteger: (value: any) => !Validation.isInteger(value),
      sxyMobilePhone: (value: any) => !Validation.isMobilePhone(value),
      sxyNumber: (value: any) => !Validation.isNumber(value),
      sxyPensionContractNumber: (value: any) => !Validation.isPensionContractNumber(value),
      sxyPersonalIdFi: (value: any) => !Validation.isPersonalIdFi(value),
      sxyPostalCodeFi: (value: any) => !Validation.isPostalCodeFi(value),
      sxyTaxPercent: (value: any) => !Validation.isTaxPercent(value),
      sxyTemporaryPensionContractNumber: (value: any) => !Validation.isTemporaryPensionContractNumber(value)
    };

    const coreValidator = validators[element.validator];
    if (!coreValidator) {
      return undefined;
    }

    return (value: any): string | undefined => {
      // Input is missing value => Use required value text
      if (element.getRequire() && !value) {
        return "SALAXY.VALIDATION.ValidationErrors.required";
      }
      if (coreValidator(value)) {
        return `SALAXY.VALIDATION.ValidationErrors.${element.validator}`;
      }
      return undefined;
    };
  }

  /**
   * Translates given key to the current language with default value.
   * @param key - Key for the text.
   * @param defaultValue - The default value that is returned if the key is not found.
   * Note that `null` and `undefined` (omitted parameter) function differently here:
   * Null returns undefined if the key is not found.
   * Undefined returns always a text: It uses the resolve logic of the translations provider.
   * Typically, this logic will attempt to find the text in the default language of the app and if not available,
   * returns the last part of the key with potential red exclamation mark.
   * @returns The translated value or default value as explained above.
   */
  public getText(key: string, defaultValue = ""): string {
    if (defaultValue === undefined) {
      return Translations.get(key);
    }
    return Translations.getWithDefault(key, defaultValue);
  }



  /**
   * Gets a unit text for an input that has a given format.
   * Units are short texts in the input e.g. %, km, €, h, min, days, weeks.
   * @param format - Format of the input either in the schema or explicitly as input component attribute.
   */
  public getUnitText(format: string): string {
    return this.getText(`SALAXY.ENUM.Unit.${format}.shortIndicator`, "");
  }

  /**
   * Gets a description text for an enumeration value in current language.
   * Note that this may not be available for all enumerations by design.
   * @param enumType - Name of the Type of the enumeration.
   * @param enumValue Name/value of the member for which the description is fetched.
   * @returns Language specific text form translations or undefined if none is found.
   */
  public getEnumDescr(enumType: string, enumValue: string): string | undefined {
    return EnumerationsLogic.getEnumDescr(enumType, enumValue);
  }

  /**
   * Gets a label text for an enumeration value.
   * @param enumType Name of the Type of the enumeration.
   * @param enumValue Name/value of the member for which the label is fetched.
   * @returns Language specific text form translations or the memberName text if no translation is found.
   */
  public getEnumLabel(enumType: string, enumValue: string): string {
    return EnumerationsLogic.getEnumLabel(enumType, enumValue);
  }

  /**
   * Gets the metadata (texts etc.) for an enumeration and its values.
   * @param enumName -  Name of the Type of the enumeration.
   */
  public getEnumMetadata(enumName: string): EnumMetadata | undefined {
    return EnumerationsLogic.getEnumMetadata(enumName);
  }

}